import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './store/authStore';
import api from './api/axios';

import Login from './pages/Login';
import POS from './pages/POS';
import Inventory from './pages/Inventory';
import MenuManagement from './pages/MenuManagement';
import Orders from './pages/Orders';
import Reports from './pages/Reports';
import StaffManagement from './pages/StaffManagement';
import StaffPerformance from './pages/StaffPerformance';
import StaffPortal from './pages/StaffPortal';
import Settings from './pages/Settings';
import UITemplates from './pages/UITemplates';
import VoiceTest from './pages/VoiceTest';
import Expenses from './pages/Expenses';
import DailyReconciliation from './pages/DailyReconciliation';
import LiveActivityPanel from './components/LiveActivityPanel';
import AIAssistant from './pages/AIAssistant';
import Layout from './components/Layout';

const IndexRedirect = () => {
  const { isStaff, isCashier, isAdmin } = useAuthStore();
  return <Navigate to={(isStaff() || isCashier()) ? '/staff-portal' : isAdmin() ? '/reports' : '/pos'} replace />;
};

const InventoryAccessRoute = () => {
  const { hasPermission } = useAuthStore();
  if (hasPermission('inventory', 'view') || hasPermission('wastage', 'view')) {
    return <Inventory />;
  }
  return <Navigate to="/" replace />;
};

const SettingsAccessRoute = () => {
  const { hasPermission, isAdmin } = useAuthStore();
  if (
    hasPermission('settings', 'view') ||
    hasPermission('users', 'view') ||
    hasPermission('menu', 'view') ||
    isAdmin()
  ) {
    return <Settings />;
  }
  return <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children, requiredModule, requiredAction }) => {
  const { isAuthenticated, isLoading, hasPermission, isAdmin, isStaff } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredModule && requiredAction && !hasPermission(requiredModule, requiredAction)) {
    if (isAdmin()) return <Navigate to="/reports" replace />;
    const { isCashier } = useAuthStore.getState();
    if (isStaff() || isCashier()) return <Navigate to="/staff-portal" replace />;
    return <Navigate to="/pos" replace />;
  }

  return children;
};

function App() {
  const { checkAuth, isLoading, isAuthenticated } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/settings/').then(res => {
        if (res.data?.date_format) {
          localStorage.setItem('pos_date_format', res.data.date_format);
        }
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<Login />} />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<IndexRedirect />} />
          <Route path="dashboard" element={<Navigate to="/reports" replace />} />
          <Route path="pos" element={
            <ProtectedRoute requiredModule="pos" requiredAction="add">
              <POS />
            </ProtectedRoute>
          } />
          <Route path="inventory" element={
            <ProtectedRoute>
              <InventoryAccessRoute />
            </ProtectedRoute>
          } />
          <Route path="menu" element={
            <ProtectedRoute requiredModule="menu" requiredAction="view">
              <MenuManagement />
            </ProtectedRoute>
          } />
          <Route path="orders" element={
            <ProtectedRoute requiredModule="sales" requiredAction="view">
              <Orders />
            </ProtectedRoute>
          } />
          <Route path="wastage" element={
            <ProtectedRoute>
              <Navigate to="/inventory?tab=wastage" replace />
            </ProtectedRoute>
          } />
          <Route path="reports" element={
            <ProtectedRoute requiredModule="reports" requiredAction="view">
              <Reports />
            </ProtectedRoute>
          } />
          <Route path="users" element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=users" replace />
            </ProtectedRoute>
          } />
          <Route path="staff" element={
            <ProtectedRoute>
              <StaffManagement />
            </ProtectedRoute>
          } />
          <Route path="performance" element={<Navigate to="/staff" replace />} />
          <Route path="staff-portal" element={
            <ProtectedRoute>
              <StaffPortal />
            </ProtectedRoute>
          } />
          <Route path="acl" element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=rbac" replace />
            </ProtectedRoute>
          } />
          <Route path="settings" element={
            <ProtectedRoute>
              <SettingsAccessRoute />
            </ProtectedRoute>
          } />
          <Route path="templates" element={
            <ProtectedRoute requiredModule="settings" requiredAction="view">
              <UITemplates />
            </ProtectedRoute>
          } />
          <Route path="expenses" element={
            <ProtectedRoute requiredModule="expenses" requiredAction="view">
              <Expenses />
            </ProtectedRoute>
          } />
          <Route path="reconciliation" element={
            <ProtectedRoute requiredModule="reconciliation" requiredAction="view">
              <DailyReconciliation />
            </ProtectedRoute>
          } />
          <Route path="stock-suggestions" element={
            <ProtectedRoute>
              <Navigate to="/reports?tab=stock_suggestion" replace />
            </ProtectedRoute>
          } />
          <Route path="activity" element={
            <ProtectedRoute requiredModule="activity_log" requiredAction="view">
              <LiveActivityPanel isFullPage={true} />
            </ProtectedRoute>
          } />
          <Route path="media" element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=media" replace />
            </ProtectedRoute>
          } />
          <Route path="ai-assistant" element={
            <ProtectedRoute requiredModule="ai_assistant" requiredAction="view">
              <AIAssistant />
            </ProtectedRoute>
          } />
          {process.env.NODE_ENV === 'development' && (
            <Route path="voice-test" element={<VoiceTest />} />
          )}
        </Route>
        
        <Route path="*" element={<Navigate to="/reports" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
